import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertDisplayType } from "@commonEnums/AlertDisplayType";
import { AlertService } from '@commonServices/alert.service';
import { RequestService } from '@commonServices/request.service';
import { AlertPositionType } from '@enums/alertPositionType';
import { AdministrationCategoryGroupDto, Client as CatalogAPI, FileParameter, ManufacturerAndCatalogBasicDto, ManufacturerAndCatalogBasicUpdateDto, ManufacturerAndCatalogContactDto, ManufacturerAndCatalogLogosDto, ManufacturerAndCatalogSettingsDto, ManufacturerAndCatalogSocialMediaDto, PrintLogoStatus } from "@interfaces/HttpClient/CatalogApiModels";
import { firstValueFrom } from 'rxjs';
import { AdministrationService } from './administration.service';
import { TranslationService } from './translation.service';

@Injectable()
export class ManufacturerAndCatalogService {

	constructor(
		private administrationService: AdministrationService,
		private requestService: RequestService,
		private catalogApi: CatalogAPI,
		private translationService: TranslationService,
		private alertService: AlertService<AlertPositionType>
	) { }

	async getManufacturerAndCatalogBasic(
		catalogId?: number
	): Promise<ManufacturerAndCatalogBasicDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_GetManufacturerAndCatalogBasic(id))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async updateManufacturerAndCatalogBasic(
		data: ManufacturerAndCatalogBasicUpdateDto,
		catalogId?: number
	): Promise<ManufacturerAndCatalogBasicDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			const update = await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_UpdateManufacturerAndCatalogBasic(id, data))
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Success, this.translationService.getByKey(
				"catalog.company.catalog.save"
			))
			return update
		} catch (e) {

			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getManufacturerAndCatalogCategories(
		catalogId?: number
	): Promise<AdministrationCategoryGroupDto[]> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_GetCategoryGroupsWithCategories(id))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getManufacturerAndCatalogContact(
		catalogId?: number
	): Promise<ManufacturerAndCatalogContactDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_GetManufacturerAndCatalogContact(id))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async updateManufacturerAndCatalogContact(
		data: ManufacturerAndCatalogContactDto,
		catalogId?: number
	): Promise<ManufacturerAndCatalogContactDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			const update = await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_UpdateManufacturerAndCatalogContact(id, data))
			this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Success, this.translationService.getByKey(
				"catalog.company.contact.save"
			))
			return update;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getManufacturerAndCatalogSocialMedia(catalogId?: number): Promise<ManufacturerAndCatalogSocialMediaDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_GetManufacturerAndCatalogSocialMedia(id))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async updateManufacturerAndCatalogSocialMedia(data: ManufacturerAndCatalogSocialMediaDto): Promise<ManufacturerAndCatalogSocialMediaDto> {
		try {
			const update = await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_UpdateManufacturerAndCatalogSocialMedia(data))
			if (update.invalidSocialMedias.length === 0) {
				this.alertService.sendMessage(AlertPositionType.Global, AlertDisplayType.Success, this.translationService.getByKey(
					"catalog.company.social-media.save"
				))
			}
			return update;
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getManufacturerAndCatalogLogo(
		catalogId?: number
	): Promise<ManufacturerAndCatalogLogosDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi.manufacturerAndCatalog_GetManufacturerAndCatalogLogo(id))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async updateManufacturerAndCatalogLogoSmall(
		originalFile?: FileParameter | null | undefined,
		croppedFile?: FileParameter | null | undefined,
		catalogId?: number
	): Promise<ManufacturerAndCatalogLogosDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi.manufacturerAndCatalog_UpdateManufacturerAndCatalogLogoSmall(id, croppedFile, originalFile))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async updateManufacturerAndCatalogLogoLarge(
		originalFile?: FileParameter | null | undefined,
		croppedFile?: FileParameter | null | undefined,
		catalogId?: number
	): Promise<ManufacturerAndCatalogLogosDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi.manufacturerAndCatalog_UpdateManufacturerAndCatalogLogoLarge(id, croppedFile, originalFile))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async updatePrintLogo(
		file: FileParameter,
		catalogId?: number
	): Promise<ManufacturerAndCatalogLogosDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi
				.manufacturerAndCatalog_UpdateManufacturerAndCatalogLogoPrint(id, file))

		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getContactFormCategories(catalogId: number): Promise<ManufacturerAndCatalogSettingsDto> {
		try {
			return await firstValueFrom(this.catalogApi.manufacturerAndCatalog_GetManufacturerAndCatalogSettings(catalogId))
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: 204, return: undefined }
			]);
		}
	}

	async getLogoInformation(catalogId?: number) {
		const id = catalogId ? catalogId : this.administrationService.getId();
		return firstValueFrom(this.catalogApi.manufacturerAndCatalog_GetManufacturerAndCatalogLogosInformation(id));
	}

	async updatePrintLogoStatus(status: PrintLogoStatus, catalogId?: number) {
		const id = catalogId ? catalogId : this.administrationService.getId();
		return firstValueFrom(this.catalogApi.manufacturerAndCatalog_UpdatePrintLogoStatus(id, status));
	}

	async deletePrintLogo(catalogId?: number) {
		const id = catalogId ? catalogId : this.administrationService.getId();
		return firstValueFrom(this.catalogApi.manufacturerAndCatalog_DeletePrintLogo(id));
	}
}
